import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Plus, Send, Upload, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import { TableSkeleton } from "@/components/common/Loading";
import Pagination from "@/components/common/Pagination";

const PAGE_LIMIT = 25;

export default function ReviewRequests() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sending, setSending] = useState(false);
  const searchTimer = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const load = useCallback(async (p = 1, q = debouncedSearch) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT };
      if (q) params.search = q;
      const { data } = await api.get("/review-requests", { params });
      setRows(arr(data?.data));
      setPagination({ total: data.total, page: data.page, totalPages: data.totalPages });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => { load(1, debouncedSearch); }, [debouncedSearch]);

  const handlePage = (p) => { setPage(p); load(p, debouncedSearch); };

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id || r._id));

  const sendRequests = async (via) => {
    if (!selected.length) return toast.error("Select at least one customer");
    setSending(true);
    try {
      const { data } = await api.post("/review-requests/send", { ids: selected, via });
      toast.success(data.message || `Sent ${data.sent} request(s)`);
      setSelected([]);
      load(page, debouncedSearch);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const deleteReq = async (id) => {
    try {
      await api.delete(`/review-requests/${id}`);
      toast.success("Deleted");
      load(page, debouncedSearch);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review Requests"
        subtitle="Send review request messages to your customers"
        icon={MessageSquare}
        actions={
          <div className="flex gap-2">
            {selected.length > 0 && (
              <>
                <button onClick={() => sendRequests("whatsapp")} disabled={sending} className="btn-primary flex items-center gap-1 text-sm">
                  <Send className="h-4 w-4" /> WhatsApp ({selected.length})
                </button>
                <button onClick={() => sendRequests("email")} disabled={sending} className="btn-outline flex items-center gap-1 text-sm">
                  <Send className="h-4 w-4" /> Email ({selected.length})
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Search bar */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search name, phone, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No customers yet"
          description={debouncedSearch ? "No results match your search." : "Add customers to start sending review requests."}
        />
      ) : (
        <div className="card-surface overflow-hidden p-0">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">
                    <input type="checkbox" checked={selected.length === rows.length && rows.length > 0} onChange={toggleAll} className="rounded" />
                  </th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Sent Via</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((r) => {
                  const id = r.id || r._id;
                  return (
                    <tr key={id} className="text-sm hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.includes(id)} onChange={() => toggleSelect(id)} className="rounded" />
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">{r.customerName}</td>
                      <td className="px-4 py-3 text-slate-600">{r.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{r.service || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 capitalize text-slate-500">{r.sentVia || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteReq(id)} className="text-xs text-red-500 hover:underline">Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-4">
            <Pagination page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} limit={PAGE_LIMIT} onPage={handlePage} />
          </div>
        </div>
      )}
    </div>
  );
}
